
import { Order, Driver, SystemConfig, OperatingContext, DeliveryProvider } from '../types';
import { DistanceService } from './logistics/core/distanceService';
import { calculateInternalCostV2 } from './logistics/costs/predictive';
import { findBestProviderQuote } from './logistics/costs/calculator';

export interface RadiusStats {
    radius: string;
    avgRealCost: number;
    avgCustomerFee: number;
    gap: number;
    orderCount: number;
}

export interface NeighborhoodStats {
    name: string;
    orderCount: number;
    totalRevenue: number;
    ownFleetOrders: number;
    thirdPartyOrders: number;
}

export interface AnalyticsData {
    totalOrders: number;
    totalRevenue: number;
    totalLogisticsCost: number;
    netProfit: number;
    avgTicket: number;
    avgDeliveryTime: number;
    slaSuccessRate: number; // % pedidos sem atraso
    avgServiceTime: number; // Tempo na porta do cliente
    profitMarginOnFee: number; // Lucro apenas na taxa de entrega
    neighborhoods: NeighborhoodStats[];
    ordersByHour: number[];
    deliveryMethods: { own: number, third: number };
    pricingIntelligence: Record<string, RadiusStats>;
    lossAreas: { lat: number; lng: number; loss: number; address: string }[];
}

export const generateAnalytics = (orders: Order[], config: SystemConfig, drivers: Driver[]): AnalyticsData => {
    const stats: AnalyticsData = {
        totalOrders: orders.length,
        totalRevenue: 0,
        totalLogisticsCost: 0,
        netProfit: 0,
        avgTicket: 0,
        avgDeliveryTime: 0,
        slaSuccessRate: 0,
        avgServiceTime: 0,
        profitMarginOnFee: 0,
        neighborhoods: [],
        ordersByHour: new Array(24).fill(0),
        deliveryMethods: { own: 0, third: 0 },
        pricingIntelligence: {},
        lossAreas: []
    };

    if (orders.length === 0) return stats;

    let totalTravelTime = 0;
    let totalServiceTime = 0;
    let onTimeOrders = 0;
    let totalFeeRevenue = 0;

    // Categorias de Raio para Inteligência de Preço
    const radiusBuckets = ["0-2km", "2-4km", "4-6km", "6-8km", "8-10km", "10-12km", "12-14km", "14-16km", "16km+"];
    for (const b of radiusBuckets) {
        stats.pricingIntelligence[b] = { radius: b, avgRealCost: 0, avgCustomerFee: 0, gap: 0, orderCount: 0 };
    }

    // Preparar contexto operacional para cálculo de terceiros
    const operatingContext: OperatingContext = {
        rainMode: 'off',
        isHighTraffic: false,
        surgeMultiplier: 1
    };

    // Preparar providers para cálculo de terceiro
    const providers: DeliveryProvider[] = [];

    orders.forEach(order => {
        const revenue = order.orderValue || 0;
        const customerFee = order.deliveryFee || 0;
        
        // v50.2: CÁLCULO DE CUSTO REAL CORRIGIDO
        // Para frota própria: usar lógica completa do sistema (diária + custo/minuto + taxa fixa)
        // Para terceiros: usar cotação do sistema (tabela ou API live)
        let realCost: number;

        if (order.status === 'dispatched_3rd_party') {
            // App Terceiro: usar cotação do sistema principal
            const dist = order.distance || DistanceService.getDistanceSync(config.businessLat, config.businessLng, order.lat || 0, order.lng || 0);
            const quote = findBestProviderQuote(dist, providers, operatingContext, !!order.isManualCost, config);
            realCost = quote.price;
        } else {
            // Frota Própria: usar cálculo completo com telemetry ou estimativa
            const dist = order.distance || DistanceService.getDistanceSync(config.businessLat, config.businessLng, order.lat || 0, order.lng || 0);
            
            // Calcular tempo baseado em telemetria real ou estimativa
            let outboundMinutes = 0;
            let interMinutes = 0;
            let returnMinutes = 0;

            if (order.departedHubAt && order.arrivedAt && order.deliveredAt) {
                // Telemetria real disponível - usar tempo real
                outboundMinutes = (order.arrivedAt - order.departedHubAt) / 60000;
                interMinutes = 0; // Pedido único
                returnMinutes = outboundMinutes; // Retorno estimado igual à ida
            } else {
                // Estimativa baseada em distância e velocidade média
                const avgSpeedKmH = 25; // Velocidade média urbana
                outboundMinutes = (dist / avgSpeedKmH) * 60;
                returnMinutes = outboundMinutes;
            }

            // Usar calculateInternalCostV2 para custo real completo
            const costResult = calculateInternalCostV2(
                { outbound: outboundMinutes, inter: interMinutes, return: returnMinutes },
                !order.estimatedDuration, // isHeuristic se não tem duração estimada
                1, // count = 1 pedido
                1, // loadFactor = 1
                config,
                false, // isDriverIdle = false (não estamos avaliando driver específico)
                1.0, // driverSpeedFactor = 1.0
                drivers
            );

            realCost = costResult.cashCost;
        }

        stats.totalRevenue += revenue;
        stats.totalLogisticsCost += realCost;
        totalFeeRevenue += customerFee;

        // 1. SLA & Tempos
        const createdAt = order.createdAt;
        const deliveredAt = order.deliveredAt || Date.now();
        const orderDuration = (deliveredAt - createdAt) / 60000;
        
        // Calcular tempo de viagem real (deslocamento), não tempo total do pedido
        let travelTimeMinutes = 0;
        if (order.departedHubAt && order.deliveredAt) {
            // Telemetria disponível - tempo de saída até entrega
            travelTimeMinutes = (order.deliveredAt - order.departedHubAt) / 60000;
        } else {
            // Fallback: usar duração total
            travelTimeMinutes = orderDuration;
        }
        
        const hasValidTimeLimit = order.timeLimit && order.timeLimit > 0;
        if (hasValidTimeLimit && travelTimeMinutes <= order.timeLimit) onTimeOrders++;
        
        totalTravelTime += travelTimeMinutes;

        // v50.3: Popula deliveryMethods
        if (order.status === 'dispatched_3rd_party') {
            stats.deliveryMethods.third++;
        } else {
            stats.deliveryMethods.own++;
        }

        // v50.3: Popula ordersByHour
        const orderHour = new Date(createdAt).getHours();
        stats.ordersByHour[orderHour]++;

        // v50.3: Popula neighborhoods (agrupamento por bairro - normalizado para evitar duplicação)
        const neighborhood = (order.neighborhood || 'Não identificado').trim().toLowerCase();
        const displayName = order.neighborhood || 'Não identificado';
        const existingNeighborhood = stats.neighborhoods.find(n => n.name.toLowerCase() === neighborhood);
        
        if (existingNeighborhood) {
            existingNeighborhood.orderCount++;
            existingNeighborhood.totalRevenue += revenue;
            if (order.status === 'dispatched_3rd_party') {
                existingNeighborhood.thirdPartyOrders++;
            } else {
                existingNeighborhood.ownFleetOrders++;
            }
        } else {
            stats.neighborhoods.push({
                name: displayName,
                orderCount: 1,
                totalRevenue: revenue,
                ownFleetOrders: order.status === 'dispatched_3rd_party' ? 0 : 1,
                thirdPartyOrders: order.status === 'dispatched_3rd_party' ? 1 : 0
            });
        }

        if (order.deliveredAt && order.arrivedAt) {
            totalServiceTime += (order.deliveredAt - order.arrivedAt) / 60000;
        }

        // 2. Localização de Prejuízo (Loss Mapping)
        if (customerFee < realCost) {
            stats.lossAreas.push({
                lat: order.lat || 0,
                lng: order.lng || 0,
                loss: realCost - customerFee,
                address: order.streetAddress
            });
        }

        // 3. Inteligência de Raio (Usa centralizador DistanceService)
        const dist = order.distance || DistanceService.getDistanceSync(config.businessLat, config.businessLng, order.lat || 0, order.lng || 0);
        let bucket = "16km+";
        if (dist <= 2) bucket = "0-2km";
        else if (dist <= 4) bucket = "2-4km";
        else if (dist <= 6) bucket = "4-6km";
        else if (dist <= 8) bucket = "6-8km";
        else if (dist <= 10) bucket = "8-10km";
        else if (dist <= 12) bucket = "10-12km";
        else if (dist <= 14) bucket = "12-14km";
        else if (dist <= 16) bucket = "14-16km";

        const b = stats.pricingIntelligence[bucket];
        b.orderCount++;
        b.avgRealCost += realCost;
        b.avgCustomerFee += customerFee;
    });

    // Finalização dos cálculos
    stats.netProfit = stats.totalRevenue - stats.totalLogisticsCost;
    stats.avgTicket = stats.totalOrders > 0 ? stats.totalRevenue / stats.totalOrders : 0;
    stats.slaSuccessRate = stats.totalOrders > 0 ? (onTimeOrders / stats.totalOrders) * 100 : 0;
    stats.avgDeliveryTime = stats.totalOrders > 0 ? totalTravelTime / stats.totalOrders : 0;

    // v49.1: Fix para avgServiceTime - Considerar apenas pedidos com telemetria válida (Frota Própria)
    const validServiceTimeOrders = orders.filter(o => o.deliveredAt && o.arrivedAt && o.driverId).length;
    stats.avgServiceTime = validServiceTimeOrders > 0 ? totalServiceTime / validServiceTimeOrders : 0;

    // v50.3: Correção de profitMarginOnFee - usar divisão por zero
    stats.profitMarginOnFee = totalFeeRevenue > 0 ? ((totalFeeRevenue - stats.totalLogisticsCost) / totalFeeRevenue) * 100 : 0;

    // v50.3: Proteger contra NaN em avgTicket e avgDeliveryTime
    if (!Number.isFinite(stats.avgTicket)) stats.avgTicket = 0;
    if (!Number.isFinite(stats.avgDeliveryTime)) stats.avgDeliveryTime = 0;
    if (!Number.isFinite(stats.avgServiceTime)) stats.avgServiceTime = 0;
    if (!Number.isFinite(stats.slaSuccessRate)) stats.slaSuccessRate = 0;
    if (!Number.isFinite(stats.profitMarginOnFee)) stats.profitMarginOnFee = 0;

    // Normalizar Inteligência de Raio
    const radiusKeys = Object.keys(stats.pricingIntelligence);
    for (const key of radiusKeys) {
        const b = stats.pricingIntelligence[key];
        if (b.orderCount > 0) {
            b.avgRealCost /= b.orderCount;
            b.avgCustomerFee /= b.orderCount;
            b.gap = b.avgRealCost - b.avgCustomerFee;
        }
    }

    return stats;
};
